import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Período seleccionado — ÚNICA fuente de verdad.
 *
 * Antes cada pantalla guardaba su propio mes con `useState`. Al volver de
 * Reportes o de Liquidación, el dashboard se remontaba y arrancaba de cero: el
 * usuario creía seguir en Febrero 2027 y en realidad estaba en el mes actual.
 * Eso no es una molestia visual — es consultar o exportar el período
 * equivocado sin enterarse.
 *
 * El período vive acá, por encima de las pantallas, así que sobrevive a que
 * cualquiera de ellas se monte y desmonte. Ninguna pantalla vuelve a
 * inicializarlo por su cuenta: lo leen de este contexto.
 *
 * ## Al recargar la aplicación
 *
 * Vuelve al MES EN CURSO, deliberadamente, y no se persiste.
 *
 * Persistirlo tendría el problema opuesto al que se está arreglando: alguien
 * que dejó una pestaña abierta en Febrero y vuelve semanas después seguiría en
 * Febrero sin haberlo pedido. El mes en curso es un valor definido y
 * predecible, y las tres pantallas lo muestran en su encabezado. Lo que no
 * puede pasar —y ya no pasa— es que el período cambie solo mientras se navega.
 */

export interface Period {
  year: number;
  month: number;
}

interface PeriodState extends Period {
  setPeriod: (p: Period) => void;
  /** Avanza o retrocede meses cruzando el fin de año correctamente. */
  shiftMonth: (delta: number) => void;
}

const PeriodContext = createContext<PeriodState | null>(null);

export function currentPeriod(now: Date = new Date()): Period {
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

/** Suma meses a un período. Extraída para poder probarla sin montar nada. */
export function shiftPeriod(p: Period, delta: number): Period {
  // Se cuenta en meses absolutos desde el año 0: así diciembre → enero y
  // enero → diciembre salen sin casos especiales.
  const total = p.year * 12 + (p.month - 1) + delta;
  return { year: Math.floor(total / 12), month: (total % 12) + 1 };
}

export function PeriodProvider({
  children,
  initial,
}: {
  children: ReactNode;
  /** Sólo para pruebas: en la aplicación arranca en el mes en curso. */
  initial?: Period;
}) {
  const [period, setPeriod] = useState<Period>(() => initial ?? currentPeriod());

  const value = useMemo<PeriodState>(
    () => ({
      year: period.year,
      month: period.month,
      setPeriod,
      shiftMonth: (delta) => setPeriod((p) => shiftPeriod(p, delta)),
    }),
    [period],
  );

  return <PeriodContext.Provider value={value}>{children}</PeriodContext.Provider>;
}

export function usePeriod(): PeriodState {
  const ctx = useContext(PeriodContext);
  if (!ctx) {
    throw new Error('usePeriod debe usarse dentro de PeriodProvider');
  }
  return ctx;
}
